import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { useChartSocket, type ChartAgent } from '../hooks/useChartSocket';
import { MAX_ASSISTANT_THREADS, usePrefs, type AssistantThread } from '../shell/prefs';

/**
 * The assistant, hoisted out of any one screen.
 *
 * It used to live inside the single page component, which is why it could only
 * ever talk about whatever that page happened to be showing. Now the panel and
 * its socket belong to the shell: the builder *registers* its live chart spec
 * with it (so the agent edits exactly what is on screen), and everything else
 * simply talks to it.
 */

export type AssistantTarget = 'chart' | 'dashboard';

interface AssistantContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  agent: ChartAgent;
  target: AssistantTarget;
  setTarget: (target: AssistantTarget) => void;
  /** In dashboard mode, whether a follow-up edits the open board or builds a new one. */
  editBoard: boolean;
  setEditBoard: (edit: boolean) => void;
  /** True while the socket is connected to a saved chart's thread. */
  editingChartId: string | undefined;
  /**
   * The builder calls this to make its on-screen chart the agent's subject.
   * Returns the unbind function; call it on unmount.
   */
  bindChart: (chartId: string | undefined, getSpec: () => Record<string, unknown> | null) => () => void;
  /**
   * The board on screen, so "change this board" means THIS board.
   *
   * Without it the panel could only edit a board the agent had itself just
   * built: the board detail page's "Change with the assistant" opened the panel
   * and named nothing, so the next message built a SECOND board instead of
   * changing the one the person was looking at (AG-2039). Mirrors bindChart —
   * the page registers while mounted and unregisters on unmount.
   */
  bindDashboard: (dashboardId: string | undefined, name?: string) => () => void;
  /** The registered board, when a board page is mounted. */
  boundDashboard: { id: string; name?: string } | undefined;
  /** Ask the assistant something without typing it (empty states, suggestions). */
  ask: (prompt: string, target?: AssistantTarget) => void;
  /** Past workspace conversations, newest first — never the chart-bound ones,
   *  which belong to their chart rather than to this list. */
  threads: AssistantThread[];
  /** The workspace thread currently open, when one is (not while editing a chart). */
  activeThreadId: string | null;
  /** Whether the live workspace conversation has anything in it — so a caller
   *  that is STARTING something (AG-2438) can begin a new one instead of
   *  appending to somebody's last build. */
  liveThreadHasMessages: boolean;
  /** Leave the current conversation listed and begin a new one. */
  newThread: () => void;
  /** Reopen a past conversation. The server restores its messages. */
  openThread: (id: string) => void;
  /** Forget one conversation. */
  deleteThread: (id: string) => void;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

/**
 * The thread list with this conversation recorded, newest first (AG-2438).
 *
 * Exported and pure so the rule can be tested as the rule rather than as a
 * re-implementation of it beside the component.
 *
 * ⭐ THE TITLE IS THIS THREAD'S OWN FIRST MESSAGE. It used to be
 * `prior?.title || title`, which pinned the name for the life of the id — so a
 * reused conversation still read "I have provided you data source of global
 * reve…" from two days earlier while its timestamp moved. The prior title is
 * the FALLBACK now, for a thread restored from the server before its transcript
 * has arrived and there is nothing yet to name it by.
 */
export function withThread(
  threads: AssistantThread[],
  id: string,
  title: string,
  target: AssistantTarget,
  now: number,
): AssistantThread[] {
  const prior = threads.find((t) => t.id === id);
  const row: AssistantThread = {
    id,
    title: (title || prior?.title || '').slice(0, 120),
    target,
    updatedAt: now,
  };
  return [row, ...threads.filter((t) => t.id !== id)].slice(0, MAX_ASSISTANT_THREADS);
}

/** A workspace-scoped thread id for conversations not attached to a saved chart. */
function newWorkspaceSession(): string {
  return `ws-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function AssistantProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const { prefs, set: setPref } = usePrefs();
  const [open, setOpenState] = useState(prefs.assistantOpen);
  const [target, setTarget] = useState<AssistantTarget>('chart');
  const [editBoard, setEditBoard] = useState(true);

  // What the builder has registered, if a builder is mounted.
  const [boundChartId, setBoundChartId] = useState<string | undefined>(undefined);
  // What the board page has registered, if a board is on screen.
  const [boundDashboard, setBoundDashboard] = useState<{ id: string; name?: string } | undefined>(undefined);
  const specSourceRef = useRef<(() => Record<string, unknown> | null) | null>(null);

  // Thread id = the chart being edited (so its conversation persists through the
  // server-side checkpointer), otherwise the workspace thread.
  //
  // The workspace id is PERSISTED. It used to be minted per mount, so every page
  // load asked the checkpointer for a thread that had never existed — which is
  // why "Your conversation is kept" was never true across a reload, however
  // faithfully the server kept it. `chart-<id>` was already stable for the same
  // reason; this is the workspace equivalent.
  const [workspaceSession, setWorkspaceSession] = useState(
    () => prefs.workspaceSession || newWorkspaceSession(),
  );
  useEffect(() => {
    if (prefs.workspaceSession !== workspaceSession) {
      setPref('workspaceSession', workspaceSession);
    }
  }, [prefs.workspaceSession, workspaceSession, setPref]);
  const sessionId = boundChartId ? `chart-${boundChartId}` : workspaceSession;

  const agent = useChartSocket(open ? sessionId : null, {
    chartId: boundChartId,
    getSpec: () => specSourceRef.current?.() ?? null,
  });

  const setOpen = useCallback(
    (next: boolean) => {
      setOpenState(next);
      setPref('assistantOpen', next);
    },
    [setPref],
  );

  const toggle = useCallback(() => setOpen(!open), [open, setOpen]);

  const bindChart = useCallback(
    (chartId: string | undefined, getSpec: () => Record<string, unknown> | null) => {
      specSourceRef.current = getSpec;
      setBoundChartId(chartId);
      return () => {
        specSourceRef.current = null;
        setBoundChartId(undefined);
      };
    },
    [],
  );

  const bindDashboard = useCallback((dashboardId: string | undefined, name?: string) => {
    setBoundDashboard(dashboardId ? { id: dashboardId, name } : undefined);
    return () => setBoundDashboard(undefined);
  }, []);

  const ask = useCallback(
    (prompt: string, nextTarget?: AssistantTarget) => {
      if (nextTarget) setTarget(nextTarget);
      setOpen(true);
      // The socket may still be connecting; queue by retrying once it opens.
      const trySend = (attempt = 0) => {
        if (agent.connected) {
          if ((nextTarget ?? target) === 'dashboard') agent.sendDashboard(prompt);
          else agent.send(prompt);
          return;
        }
        if (attempt < 40) window.setTimeout(() => trySend(attempt + 1), 250);
      };
      trySend();
    },
    [agent, setOpen, target],
  );

  // When the agent creates or updates a dashboard (and its charts), refresh the
  // cached lists so the board and its charts appear immediately and stay editable
  // everywhere else in the product.
  const dashNonce = agent.dashboard?.nonce;
  const dashId = agent.dashboard?.id;
  useEffect(() => {
    if (!dashId) return;
    qc.invalidateQueries({ queryKey: ['analytics', 'dashboards'] });
    qc.invalidateQueries({ queryKey: ['analytics', 'dashboard', dashId] });
    qc.invalidateQueries({ queryKey: ['analytics', 'charts'] });
  }, [dashNonce, dashId, qc]);

  const threads = prefs.assistantThreads;

  /** Record the live workspace thread in the list, newest first.
   *
   *  Called as the conversation gets its first user message, so a thread is
   *  listed only once it has something in it — an empty one is noise, and the
   *  first thing a person said is also the only sensible title. */
  const rememberThread = useCallback(
    (id: string, title: string, forTarget: AssistantTarget) => {
      setPref('assistantThreads', withThread(threads, id, title, forTarget, Date.now()));
    },
    [threads, setPref],
  );

  // The first user message in the live workspace thread names it and puts it in
  // the list. Reading it off the transcript rather than the send path means a
  // thread restored from the server is listed too.
  const firstUserText = !boundChartId
    ? (agent.messages.find((m) => m.role === 'user')?.text ?? '')
    : '';
  const listedId = threads.find((t) => t.id === workspaceSession)?.id;
  useEffect(() => {
    if (boundChartId || !firstUserText) return;
    if (listedId === workspaceSession) return;
    rememberThread(workspaceSession, firstUserText, target);
  }, [boundChartId, firstUserText, listedId, workspaceSession, target, rememberThread]);

  /** Begin a new conversation, LEAVING the current one listed.
   *
   *  This used to be `clear`, which rotated the workspace id and dropped the old
   *  conversation on the floor: the server still held it under the old id, but
   *  nothing in the product could name it, so it was gone as far as anyone using
   *  it was concerned (AG-2038). Now the id is remembered before it is replaced.
   */
  const newThread = useCallback(() => {
    agent.clear();
    setWorkspaceSession(newWorkspaceSession());
  }, [agent]);

  const openThread = useCallback(
    (id: string) => {
      if (!id || id === workspaceSession) return;
      agent.clear();
      setWorkspaceSession(id);
      setOpen(true);
    },
    [agent, workspaceSession, setOpen],
  );

  const deleteThread = useCallback(
    (id: string) => {
      setPref('assistantThreads', threads.filter((t) => t.id !== id));
      // Removing the one you are in leaves the panel on a conversation with no
      // record, so start a fresh one rather than a half-detached state.
      if (id === workspaceSession) {
        agent.clear();
        setWorkspaceSession(newWorkspaceSession());
      }
    },
    [threads, setPref, workspaceSession, agent],
  );

  // "Clear" is now "start a new conversation": the transcript blanks, and what
  // was on screen stays reachable in the history list.
  const agentWithReset = useMemo(
    () => ({
      ...agent,
      clear: () => {
        agent.clear();
        if (!boundChartId) setWorkspaceSession(newWorkspaceSession());
      },
    }),
    [agent, boundChartId],
  );

  const value = useMemo<AssistantContextValue>(
    () => ({
      open,
      setOpen,
      toggle,
      agent: agentWithReset,
      target,
      setTarget,
      editBoard,
      setEditBoard,
      editingChartId: boundChartId,
      bindChart,
      bindDashboard,
      boundDashboard,
      ask,
      threads,
      liveThreadHasMessages: Boolean(firstUserText),
      activeThreadId: boundChartId ? null : workspaceSession,
      newThread,
      openThread,
      deleteThread,
    }),
    [open, setOpen, toggle, agentWithReset, target, editBoard, boundChartId, bindChart,
      bindDashboard, boundDashboard, ask,
      threads, workspaceSession, newThread, openThread, deleteThread, firstUserText],
  );

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

export function useAssistant(): AssistantContextValue {
  const ctx = useContext(AssistantContext);
  if (!ctx) throw new Error('useAssistant must be used inside AssistantProvider');
  return ctx;
}

/**
 * Register the builder's live chart as the assistant's subject for as long as the
 * builder is mounted.
 */
export function useAssistantSubject(
  chartId: string | undefined,
  getSpec: () => Record<string, unknown> | null,
) {
  const { bindChart } = useAssistant();
  // The latest getter, held so the binding below keeps its identity. Written in
  // an effect rather than during render: the assistant calls this closure when
  // somebody sends a message, which is always after the commit that changed it.
  const getSpecRef = useRef(getSpec);
  useEffect(() => {
    getSpecRef.current = getSpec;
  }, [getSpec]);
  useEffect(() => bindChart(chartId, () => getSpecRef.current()), [bindChart, chartId]);
}

/** Register the board on screen as what "change this board" refers to. */
export function useAssistantBoard(dashboardId: string | undefined, name?: string) {
  const { bindDashboard } = useAssistant();
  useEffect(() => bindDashboard(dashboardId, name), [bindDashboard, dashboardId, name]);
}
